import { dbService, STORE_PHOTOS } from './indexeddb';

export type SyncStatus = 'pendente' | 'sincronizado';

export interface PhotoRecord {
    id: string; // crypto.randomUUID()
    maquinaId: string; // id do ativo/tarefa
    nomeMaquina: string; // nome da máquina para organizar pasta no servidor
    projetoId: string;
    foto: Blob; // Blob da foto tirada
    dataHora: string; // ISO string
    status: SyncStatus;
}

export interface PendingMachineCount {
    maquinaId: string;
    nomeMaquina: string;
    count: number;
    latestDataHora: string;
}

export class PhotoRepository {
    async savePhoto(photo: Omit<PhotoRecord, 'id' | 'status'>): Promise<string> {
        const id = crypto.randomUUID();
        const record: PhotoRecord = {
            ...photo,
            id,
            status: 'pendente',
        };
        await dbService.put(STORE_PHOTOS, record);

        // Registrar Background Sync para envio automático quando conexão voltar
        if ('serviceWorker' in navigator && 'SyncManager' in window) {
            navigator.serviceWorker.ready.then((registration) => {
                return (registration as ServiceWorkerRegistration & { sync: { register: (tag: string) => Promise<void> } }).sync.register('sync-fotos');
            }).catch(() => {
                // Background Sync não suportado ou falhou — sync manual ainda disponível
            });
        }

        return id;
    }

    async getPendingPhotos(): Promise<PhotoRecord[]> {
        const all = await dbService.getAll<PhotoRecord>(STORE_PHOTOS);
        return all.filter(p => p.status === 'pendente').sort((a, b) => new Date(a.dataHora).getTime() - new Date(b.dataHora).getTime());
    }

    async getPendingCountsByMachine(projetoId: string): Promise<PendingMachineCount[]> {
        const all = await dbService.getAll<PhotoRecord>(STORE_PHOTOS);
        const pending = all.filter(p => p.status === 'pendente' && p.projetoId === projetoId);
        const grouped = new Map<string, PendingMachineCount>();

        for (const photo of pending) {
            const current = grouped.get(photo.maquinaId);
            if (!current) {
                grouped.set(photo.maquinaId, {
                    maquinaId: photo.maquinaId,
                    nomeMaquina: photo.nomeMaquina ?? photo.maquinaId,
                    count: 1,
                    latestDataHora: photo.dataHora,
                });
                continue;
            }

            current.count += 1;
            if (new Date(photo.dataHora).getTime() > new Date(current.latestDataHora).getTime()) {
                current.latestDataHora = photo.dataHora;
            }
        }

        return Array.from(grouped.values()).sort((a, b) => {
            return new Date(b.latestDataHora).getTime() - new Date(a.latestDataHora).getTime();
        });
    }

    async getPendingPhotosByMachine(
        projetoId: string,
        maquinaId: string,
        options?: { limit?: number }
    ): Promise<PhotoRecord[]> {
        const limit = options?.limit;
        const all = await dbService.getAll<PhotoRecord>(STORE_PHOTOS);
        const sorted = all
            .filter((p) => p.status === 'pendente' && p.projetoId === projetoId && p.maquinaId === maquinaId)
            .sort((a, b) => new Date(b.dataHora).getTime() - new Date(a.dataHora).getTime());

        if (!limit || limit <= 0) return sorted;
        return sorted.slice(0, limit);
    }

    async markAsSynced(id: string): Promise<void> {
        const photo = await dbService.get<PhotoRecord>(STORE_PHOTOS, id);
        if (photo) {
            photo.status = 'sincronizado';
            await dbService.put(STORE_PHOTOS, photo);
        }
    }

    async deletePhoto(id: string): Promise<void> {
        await dbService.delete(STORE_PHOTOS, id);
    }

    async getPhotosByProject(projetoId: string): Promise<PhotoRecord[]> {
        const all = await dbService.getAll<PhotoRecord>(STORE_PHOTOS);
        return all.filter(p => p.projetoId === projetoId).sort((a, b) => new Date(b.dataHora).getTime() - new Date(a.dataHora).getTime());
    }

    async deleteSyncedPhotos(): Promise<void> {
        const all = await dbService.getAll<PhotoRecord>(STORE_PHOTOS);
        const syncedIds = all.filter(p => p.status === 'sincronizado').map(p => p.id);
        for (const id of syncedIds) {
            await dbService.delete(STORE_PHOTOS, id);
        }
    }
}

export const photoRepository = new PhotoRepository();
